import { AxiosResponse } from 'axios';

import {
  CommonResponseType,
  LoginRequestType,
  LoginResponseType,
  SignUpRequestType,
  UserType,
} from './types';

import { authServiceClient } from 'config';
import { withQuery } from 'utils';

export const logInRequest = (data: LoginRequestType) => {
  return authServiceClient.post<
    LoginResponseType,
    AxiosResponse<LoginResponseType>,
    LoginRequestType
  >(`/auth/sign-in-admin`, data, {
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

export const getNotificationsRequest = ({ take, page, q }: any) => {
  return authServiceClient.get<any, AxiosResponse<any>>(
    withQuery('/notification/all-admin', { q, take, page }),
  );
};

export const getNotificationRequest = (id: any) => {
  return authServiceClient.get<any, AxiosResponse<any>>(`/notification/${id}`);
};

export const deleteNotificationRequest = (id: any) => {
  return authServiceClient.delete<any, AxiosResponse<any>>(`/notification/${id}`);
};

export const getNotificationsForAdminRequest = (id: any) => {
  return authServiceClient.get<any, AxiosResponse<any>>(`/notification/for-admin/${id}`);
};

export const getUsersRequest = ({ take, page, q, status, category, isVerify }: any) => {
  // `role` stays literal in the path: it is a fixed discriminator, not a filter.
  return authServiceClient.get<any, AxiosResponse<any>>(
    withQuery('/users?role=User', { q, take, page, status }),
  );
};

export const getDoctorsRequest = ({ take, page, q, status, category, isVerify }: any) => {
  return authServiceClient.get<any, AxiosResponse<any>>(
    withQuery('/users?role=Doctor%2fNurse', { q, take, page, status, category }),
  );
};

export const getFinancesRequest = ({ take, page, q, status, category, year }: any) => {
  return authServiceClient.get<any, AxiosResponse<any>>(
    withQuery('/finance/all', { q, take, page, status, category, year }),
  );
};

export const getArticleRequest = ({ take, page, q }: any) => {
  return authServiceClient.get<any, AxiosResponse<any>>(
    withQuery('/articles/all', { q, take, page }),
  );
};

export const getArticleByIdRequest = (id: any) => {
  return authServiceClient.get<any, AxiosResponse<any>>(`/articles/${id}`);
};

export const deleteArticleByIdRequest = (id: any) => {
  return authServiceClient.delete<any, AxiosResponse<any>>(`/articles/${id}`);
};

export const updateArticleByIdRequest = (id: any, data: any) => {
  return authServiceClient.patch<any, AxiosResponse<any>>(`/articles/${id}`, data);
};

export const getNewcomersRequest = (month: any, year: any) => {
  return authServiceClient.get<any, AxiosResponse<any>>(
    withQuery('/users/statistics/', { month, year }),
  );
};

export const getActiveUsersRequest = (month: any, year: any) => {
  return authServiceClient.get<any, AxiosResponse<any>>(
    withQuery('usersActivities', { month, year }),
  );
};

export const getBlockedUsersRequest = (month: any, year: any) => {
  return authServiceClient.get<any, AxiosResponse<any>>(
    withQuery('usersBlocked', { month, year }),
  );
};

export const getFinanceStatisticsRequest = (year: any) => {
  return authServiceClient.get<any, AxiosResponse<any>>(
    `finance/statisticsByYear/${year}`,
  );
};

export const getCommissionsRequest = () => {
  return authServiceClient.get<any, AxiosResponse<any>>(`/commission/all`);
};

export const updateCommissionsRequest = (data: any) => {
  return authServiceClient.put<any, AxiosResponse<any>>(`/commission`, data);
};

export const createArticleRequest = (data: any) => {
  return authServiceClient.post<any, AxiosResponse<any>, any>(`/articles/create`, data);
};

/**
 * The admin-editable static pages the mobile app renders: "About us" and
 * "How to deal with the platform".
 *
 * Reads are public on the server (the app hits them from its pre-login stack);
 * the PUT is admin-only and validates the key against a fixed set server-side,
 * so this cannot be used to create arbitrary pages.
 */
export const getAppContentListRequest = () => {
  return authServiceClient.get<any, AxiosResponse<any>>(`/content`);
};

export const updateAppContentRequest = (
  key: string,
  data: { contentEnglish?: string; contentArabic?: string },
) => {
  return authServiceClient.put<any, AxiosResponse<any>>(`/content/${key}`, data);
};


export const getUserRequest = (id: any) => {
  return authServiceClient.get<any, AxiosResponse<any>>(`/users/${id}`);
};

export const getDocsRequest = (id: any) => {
  return authServiceClient.get<any, AxiosResponse<any>>(`/users/docs/${id}`);
};

export const getContactRequest = () => {
  return authServiceClient.get<any, AxiosResponse<any>>(`/contactUs`);
};

export const updateContactRequest = (data: any) => {
  return authServiceClient.patch<any, AxiosResponse<any>>(`/contactUs`, data);
};

export const getPatientVisitsRequest = ({ q, take, page, id, year, month }: any) => {
  return authServiceClient.get<any, AxiosResponse<any>>(
    withQuery(`/visits/${id}`, { q, take, page, year, month }),
  );
};

export const getDoctorVisitsRequest = ({ q, take, page, id, year, month }: any) => {
  return authServiceClient.get<any, AxiosResponse<any>>(
    withQuery(`/visits/doctor/${id}`, { q, take, page, year, month }),
  );
};

export const getDoctorFinancesRequest = ({ q, take, page, id, year }: any) => {
  return authServiceClient.get<any, AxiosResponse<any>>(
    withQuery(`/finance/${id}`, { q, take, page, year }),
  );
};

export const editUserRequest = (id: any, data: any) => {
  return authServiceClient.patch<any, AxiosResponse<any>>(`/users/${id}`, data);
};

/**
 * Closes a patient's or provider's account on their behalf.
 *
 * Deliberately NOT `editUserRequest(id, { userStatus: 'Deleted' })`. That PATCH
 * only moves a column: it would leave the name, the email, the phone number and
 * every uploaded document exactly where they are, skip the dues and
 * in-flight-visit gates, and write nothing to the audit table -- an account that
 * reads as closed in the console and is not. This route runs the same
 * anonymisation the person gets when they close their own account, with the
 * administrator recorded as the actor.
 *
 * The body is optional on the server (`DeleteOwnAccountDto`, a reason of at
 * most 500 characters). It travels under `data` because that is the only way
 * axios sends a body on a DELETE.
 */
export const closeUserAccountRequest = (id: string, reason?: string) => {
  return authServiceClient.delete<any, AxiosResponse<any>>(
    `/users/${id}/account`,
    { data: reason ? { reason } : {} },
  );
};

// --- Provider approval -------------------------------------------------------
// Providers sign up as "Not verified" and cannot sign in or take work until an
// admin reviews them. These are the dedicated routes for that review.
//
// Approval deliberately does NOT go through editUserRequest. Writing
// `{userStatus: 'Active'}` through the generic PATCH is how this used to be
// done, and it could not distinguish approving a new provider from unblocking a
// banned one, could not tell when another admin had already decided, and — as
// the same route accepted `isVerify` — let a second, stale request silently
// undo the approval it had just made.

export const getPendingProvidersRequest = ({ q, take, page }: any = {}) => {
  return authServiceClient.get<any, AxiosResponse<any>>(
    withQuery(`/users/providers/pending`, { q, take, page }),
  );
};

/** Just the number, for the sidebar badge. Cheap enough to poll on navigation. */
export const getPendingProvidersCountRequest = () => {
  return authServiceClient.get<any, AxiosResponse<any>>(
    `/users/providers/pending/count`,
  );
};

export const decideProviderVerificationRequest = (
  id: string,
  decision: 'approve' | 'reject',
  reason?: string,
) => {
  return authServiceClient.patch<any, AxiosResponse<any>, any>(
    `/users/${id}/verification`,
    { decision, ...(reason ? { reason } : {}) },
  );
};

// --- Provider profile photo review -------------------------------------------
// A second, independent queue. A provider's photo is shown to patients during a
// request, so it is reviewed before it is shown: an upload lands in
// `doctor.avatarPending` and only a decision here moves it to `doctor.avatar`.
//
// Separate from verification because it happens repeatedly over an account's
// life — an established provider changing their photo must not have to be
// re-approved — and because a photo can be refused without touching the
// account behind it.

export const getPendingPhotosRequest = ({ q, take, page }: any = {}) => {
  return authServiceClient.get<any, AxiosResponse<any>>(
    withQuery(`/users/providers/photos/pending`, { q, take, page }),
  );
};

/** Just the number, for the queue badge. */
export const getPendingPhotosCountRequest = () => {
  return authServiceClient.get<any, AxiosResponse<any>>(
    `/users/providers/photos/pending/count`,
  );
};

export const decideProviderPhotoRequest = (
  id: string,
  decision: 'approve' | 'reject',
  reason?: string,
) => {
  return authServiceClient.patch<any, AxiosResponse<any>, any>(
    `/users/${id}/photo`,
    { decision, ...(reason ? { reason } : {}) },
  );
};

export const editFinanceRequest = (id: any, data: any) => {
  return authServiceClient.patch<any, AxiosResponse<any>>(`/finance/${id}`, data);
};

export const createNotificationRequest = (data: any) => {
  return authServiceClient.post<any, AxiosResponse<any>, any>(
    `/notification/create`,
    data,
  );
};

export const updateNotificationeByIdRequest = (id: any, data: any) => {
  return authServiceClient.patch<any, AxiosResponse<any>>(`/notification/${id}`, data);
};

export const signUpRequest = (data: SignUpRequestType) => {
  return authServiceClient.post<
    CommonResponseType,
    AxiosResponse<CommonResponseType>,
    SignUpRequestType
  >(`/auth/sign-up`, data);
};

export const getMeRequest = () => {
  return authServiceClient.get<UserType, AxiosResponse<UserType>>(`/auth/me`);
};

// --- Administrator management -----------------------------------------------
// `/users?role=Admin` cannot serve this list: findAll() inner-joins the Doctor
// table for every role except 'User', and an admin has no doctor row, so it
// comes back empty. These are dedicated admin-only routes.

export const getAdminsRequest = () => {
  return authServiceClient.get<any, AxiosResponse<any>>(`/users/admins`);
};

export const createAdminRequest = (data: { phone: string; password: string }) => {
  return authServiceClient.post<any, AxiosResponse<any>, any>(`/users/admins`, data);
};

export const deleteAdminRequest = (id: string) => {
  return authServiceClient.delete<any, AxiosResponse<any>>(`/users/admins/${id}`);
};

/** Force-resets ANOTHER admin's password — no current password required. */
export const resetAdminPasswordRequest = (id: string, newPassword: string) => {
  return authServiceClient.patch<any, AxiosResponse<any>, any>(
    `/users/admins/${id}/password`,
    { newPassword },
  );
};

/**
 * Changes the signed-in admin's OWN password, which does require the current
 * one. Note this is a different endpoint from `changePasswordRequest` below —
 * that one posts to /auth/reset-password and belongs to the forgot-password
 * flow driven by an emailed reset token.
 *
 * ChangePasswordDto requires `passwordConfirm` and validates it matches
 * `newPassword` server-side; omitting it fails the whole request with a 400
 * "Password mismatch" even when the two passwords the user typed agreed.
 */
export const changeOwnPasswordRequest = (data: {
  password: string;
  newPassword: string;
  passwordConfirm: string;
}) => {
  return authServiceClient.post<any, AxiosResponse<any>, any>(
    `/auth/change-password`,
    data,
  );
};

export const restorePasswordRequest = (data: any) => {
  return authServiceClient.post<
    CommonResponseType,
    AxiosResponse<CommonResponseType>,
    any
  >(`/auth/restore-password`, data);
};

export const verifyResetRequest = (data: any) => {
  return authServiceClient.post<
    CommonResponseType,
    AxiosResponse<CommonResponseType>,
    any
  >(`/auth/verify-reset`, data);
};

export type RefreshTokenResponseType = {
  accessToken: string;
  refreshToken: string;
};

/**
 * Exchanges a refresh token for a fresh token pair.
 *
 * This previously issued `GET /auth/refresh` with the token in a `refresh`
 * header. The API exposes `POST /auth/refresh-token` and reads `refreshToken`
 * from the body (RefreshTokenDto), so the old call could only ever 404 — which
 * is why automatic re-authentication never worked.
 */
export const refreshTokenRequest = (refreshToken: string) => {
  return authServiceClient.post<
    RefreshTokenResponseType,
    AxiosResponse<RefreshTokenResponseType>,
    { refreshToken: string }
  >(`/auth/refresh-token`, { refreshToken });
};

export const changePasswordRequest = (data: any) => {
  return authServiceClient.post<
    CommonResponseType,
    AxiosResponse<CommonResponseType>,
    any
  >(`/auth/reset-password`, data);
};

/**
 * How long an upload may take before the console gives up on it.
 *
 * The client's default is 30 s, which is right for JSON and wrong for a file:
 * a 25 MB video needed about 6.7 Mbit/s of upstream to finish inside it, so on
 * an ordinary office connection the request was aborted mid-upload and the
 * operator was told it failed. Six minutes is the server's own limit plus a
 * margin -- Node stops receiving a request body after 300 s, and the API then
 * pushes the file on to storage, where the SDK gives up after 60 s without an
 * answer -- so the console waits as long as the upload could still succeed,
 * and no longer. That needs about 0.7 Mbit/s for the largest video.
 *
 * Per request rather than on the client: every other call should still fail
 * fast. The token-refresh retry reuses the request's own config, so the longer
 * limit survives a retry too.
 */
const UPLOAD_TIMEOUT_MS = 6 * 60 * 1000;

/**
 * The upload limit, only when a file is actually being sent. The card form's
 * visibility toggle goes through the same PATCH with no photo, and that should
 * keep failing fast like any other small request.
 */
const uploadTimeoutFor = (data: Record<string, any>) =>
  Object.values(data).some((value) => value instanceof File)
    ? { timeout: UPLOAD_TIMEOUT_MS }
    : {};

/**
 * Medical awareness cards — the photo-and-link feed on the mobile app's
 * Helpful Information screen.
 *
 * The reads are ordinary JSON. The two writes are **multipart**, because a card
 * carries a photograph, and that is why they build a `FormData` here rather
 * than posting an object: the server names the file field `image` on its
 * interceptor, and that field name is what decides the upload is stored as a
 * publicly-readable asset instead of a signed private one.
 *
 * `Content-Type` is deliberately NOT set. The axios instance defaults every
 * request to `application/json`, and passing FormData with that header leaves
 * the body unparsed on the server — every field arrives empty and the request
 * comes back as a validation error naming fields the operator plainly filled
 * in. Setting it to `undefined` makes axios let the browser write the header,
 * which is the only way the multipart boundary is correct.
 */
const awarenessFormData = (data: Record<string, any>): FormData => {
  const form = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    // `undefined` means "not sent"; the server leaves that field alone. An
    // empty string is a real value — it is how the source name is cleared — so
    // only undefined and null are skipped.
    if (value === undefined || value === null) return;

    form.append(key, value instanceof File ? value : String(value));
  });

  return form;
};

export const getAwarenessCardsRequest = ({ take, page, q, published }: any) => {
  return authServiceClient.get<any, AxiosResponse<any>>(
    withQuery('/awareness-cards', { q, take, page, published }),
  );
};

export const createAwarenessCardRequest = (data: Record<string, any>) => {
  return authServiceClient.post<any, AxiosResponse<any>>(
    '/awareness-cards',
    awarenessFormData(data),
    { headers: { 'Content-Type': undefined }, ...uploadTimeoutFor(data) },
  );
};

export const updateAwarenessCardRequest = (id: string, data: Record<string, any>) => {
  return authServiceClient.patch<any, AxiosResponse<any>>(
    `/awareness-cards/${id}`,
    awarenessFormData(data),
    { headers: { 'Content-Type': undefined }, ...uploadTimeoutFor(data) },
  );
};

export const deleteAwarenessCardRequest = (id: string) => {
  return authServiceClient.delete<any, AxiosResponse<any>>(`/awareness-cards/${id}`);
};

/**
 * Onboarding videos: the intro clip and the two looping backdrops.
 *
 * The admin read is a separate route from the app's, and deliberately so — it
 * returns slots that are switched off, the stored URL behind the optimised one,
 * and the size guidance each slot is judged against. None of that belongs in a
 * public payload.
 */
export const getMediaSlotsRequest = () => {
  return authServiceClient.get<any, AxiosResponse<any>>('/media/admin');
};

/**
 * Point a slot at a URL, switch it on or off, or change the intro's replay
 * rule.
 *
 * Every field is optional on the server and absent means "leave it alone", so a
 * caller toggling a checkbox must send ONLY that checkbox: including the URL
 * would make a toggle re-validate and re-probe a link nobody touched, and turn
 * a transient network failure at the video's host into a refusal to switch it
 * off.
 */
export const updateMediaSlotRequest = (
  key: string,
  data: { url?: string; isEnabled?: boolean; playEveryLaunch?: boolean },
) => {
  return authServiceClient.put<any, AxiosResponse<any>>(`/media/${key}`, data);
};

/**
 * Upload a video file to a slot.
 *
 * `Content-Type: undefined` on purpose: the client sets a JSON default, and
 * leaving it in place would label a multipart body as JSON and lose the
 * boundary, so the server would parse no file at all. Undefined lets the
 * browser write the header with the boundary it generated.
 *
 * `onProgress` is threaded through because these are the largest uploads the
 * console performs — tens of megabytes, often over an ordinary office
 * connection. A spinner with no percentage on a 25 MB upload is
 * indistinguishable from a hang, and the natural response to a hang is to press
 * the button again.
 */
export const uploadMediaSlotRequest = (
  key: string,
  file: File,
  onProgress?: (percent: number) => void,
) => {
  const form = new FormData();
  form.append('video', file);

  return authServiceClient.post<any, AxiosResponse<any>>(
    `/media/${key}/upload`,
    form,
    {
      headers: { 'Content-Type': undefined },
      timeout: UPLOAD_TIMEOUT_MS,
      onUploadProgress: (event) => {
        if (!onProgress || !event.total) return;
        onProgress(Math.round((event.loaded / event.total) * 100));
      },
    },
  );
};

/** Clear a slot so the app falls back to the asset bundled in it. */
export const clearMediaSlotRequest = (key: string) => {
  return authServiceClient.delete<any, AxiosResponse<any>>(`/media/${key}`);
};

// ---------------------------------------------------------------- SMS delivery

/**
 * Is the SMS channel working?
 *
 * `live` makes the API query the gateway for the account balance rather than
 * returning the last scheduled reading. Off by default because this route is
 * also what a dashboard polls, and a diagnostic that makes an outbound call on
 * every paint becomes the thing that needs diagnosing.
 */
export const getSmsStatusRequest = (live?: boolean) => {
  return authServiceClient.get<any, AxiosResponse<any>>(
    withQuery('/admin/sms-status', { live: live ? true : undefined }),
  );
};

/**
 * What happened to each verification code we issued.
 *
 * `phone` is the filter every support conversation starts with; `outcome`
 * answers "show me everything that failed".
 */
export const getSmsLogRequest = ({ limit, offset, phone, outcome }: any) => {
  return authServiceClient.get<any, AxiosResponse<any>>(
    withQuery('/admin/sms-log', { limit, offset, phone, outcome }),
  );
};

/**
 * Give one number its code allowances back.
 *
 * The only write on the SMS routes, and the only thing in the product that can
 * lift a spent allowance. It exists because the message a spent allowance
 * produces tells the user to contact support, and until this route support had
 * no corresponding action -- so the sentence was a dead end and the real answer
 * was "wait".
 *
 * Deliberately says no number here. This used to read "the five-codes-a-day
 * cap", which was wrong on both figures: the allowance is operator-tunable,
 * the window is not a day, and there is one allowance per errand rather than
 * one per number. A comment that names a limit is a second copy of it, and
 * this file has no way to know when the first copy changes.
 *
 * `reason` is free text, capped at 500 characters by the API, and goes to the
 * audit log rather than to a column: the durable record of the release is the
 * stamp on the codes themselves. Send it as undefined rather than as an empty
 * string, so a release nobody explained does not read as one explained with
 * nothing.
 *
 * Answers `{ phone, released }`, where `released` counts the codes that were
 * still inside the window. Zero is a real and useful answer -- it means the
 * number that is blocked is not the number that was typed.
 */
export const resetSmsBudgetRequest = (phone: string, reason?: string) => {
  return authServiceClient.post<any, AxiosResponse<any>, any>(
    '/admin/sms-budget/reset',
    { phone, reason: reason?.trim() || undefined },
  );
};

// ------------------------------------------------------------ account deletions

/**
 * Who closed their account, and who tried to.
 *
 * The account itself cannot answer this: deletion anonymises the `user` row in
 * place, so the Users page shows "Deleted user" with a phone reading
 * `deleted-<uuid>`. Refusals are not on the Users page at all -- a provider who
 * was told they owed money is still an ordinary active account.
 *
 * `q` takes a name, a phone number or a user id, because support arrives with
 * whichever of the three they were given.
 */
export const getAccountDeletionsRequest = ({
  limit,
  offset,
  outcome,
  role,
  q,
}: any) => {
  return authServiceClient.get<any, AxiosResponse<any>>(
    withQuery('/admin/account-deletions', { limit, offset, outcome, role, q }),
  );
};
